import React, { useEffect, useState } from 'react';
import { chain } from 'lodash';
import { AnimatePresence, motion } from 'framer-motion';
import Chapter from '../../Chapter';
import ServicesMenuCarouselScene, {
    calculateSectionsDuration,
} from "./ServicesMenuCarouselScene";
import ServicesMenuChapterI from "./ServicesMenuChapter.interface";
import SectionHeader from './components/SectionHeader';
import { CAROUSEL_ITEM_PADDING } from './SectionLayout/SectionLayout.constants';
import { NIKE_EASE } from '../../../rise/constants/nikerise';
import setAnimationTimeout from '../../../core/utils/setAnimationTimeout';
import useOpenSection from "../../helpers/use-open-section";
import { isNumber } from "lodash";
import { secToMs } from "../../../core/utils/transforms";
import { EntryMedia } from './Entry/EntryMedia';
import { DEFAULT_TRANSITION_DURATION } from './ServiceMenuChapter.constants';

const SECOND_SCENE_DELAY_S = 5;
const THIRD_SCENE_DELAY_S = 20;
const THIRD_SCENE_EXIT_DURATION_S = 2;
const EXIT_DURATION_S = DEFAULT_TRANSITION_DURATION;

export default class ServicesMenuChapter extends Chapter<ServicesMenuChapterI> {
    component = () => {
        const [isEntryRunning, setIsEntryRunning] = useState(true);
        const [isSectionsRunning, setIsSectionsRunning] = useState(false);
        const [scene, setScene] = useState(1);
        const openSection = useOpenSection();

        useEffect(() => {
            if (isNumber(openSection)) {
                return
            }

            return setAnimationTimeout(
                () => setScene(2),
                secToMs(SECOND_SCENE_DELAY_S)
            );
        }, [openSection]);

        useEffect(() => {
            if (isNumber(openSection)) {
                return
            }

            return setAnimationTimeout(
                () => setScene(3),
                secToMs(THIRD_SCENE_DELAY_S)
            );
        }, [openSection]);

        const initSectionRunning = () => {
            setIsEntryRunning(false);
            setIsSectionsRunning(true);
        }

        useEffect(() => {
            if (isNumber(openSection)) {
                return setAnimationTimeout(initSectionRunning, 0);
            }

            return setAnimationTimeout(initSectionRunning, secToMs(THIRD_SCENE_DELAY_S) + secToMs(THIRD_SCENE_EXIT_DURATION_S));
        }, [openSection]);

        return (
            <AnimatePresence>
                {isEntryRunning && !isNumber(openSection) && (
                    <motion.div key={'entry'} style={{ position: 'relative' }}>
                        <AnimatePresence>
                            {scene === 1 ? (
                                <motion.div
                                    key='entry-title'
                                    initial={{
                                        y: 0,
                                    }}
                                    exit={{
                                        y: '-50vh',
                                    }}
                                    transition={{
                                        duration: 1,
                                        ease: NIKE_EASE,
                                    }}
                                >
                                    <SectionHeader
                                        title={this.data.subtitle}
                                        isShowDate={this.data.is_show_date}
                                        style={{ position: 'relative' }}
                                        animated
                                    />

                                    <motion.h1
                                        style={{
                                            position: 'relative',
                                            margin: 0,
                                            whiteSpace: 'pre-line',
                                            padding: `14.95vh ${CAROUSEL_ITEM_PADDING}vh`, // 287px 50px
                                            fontSize: '6.25vh', // 120px
                                            lineHeight: '6.88vh', // 132px
                                            textTransform: 'uppercase',
                                            fontFamily: 'HelveticaNowDisplay',
                                            fontWeight: '500',
                                        }}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{
                                            duration: DEFAULT_TRANSITION_DURATION,
                                            ease: NIKE_EASE,
                                        }}
                                    >
                                        {this.data.headline}
                                    </motion.h1>
                                </motion.div>
                            ) : null}

                            <motion.div
                                key='entry-media'
                                style={{ position: 'absolute', left: 0, top: 0 }}
                                initial={{ y: '50vh' }}
                                animate={{ y: 0 }}
                                transition={{
                                    duration: DEFAULT_TRANSITION_DURATION,
                                    ease: NIKE_EASE,
                                }}
                            >
                                <EntryMedia sections={this.data?.sections} scene={scene} />
                            </motion.div>
                        </AnimatePresence>
                    </motion.div>
                )}

                {isSectionsRunning && (
                    <motion.div
                        key={'sections'}
                        style={{ position: 'relative' }}
                    >
                        <ServicesMenuCarouselScene {...this.data} />
                    </motion.div>
                )}
            </AnimatePresence>
        );
    }

    fonts = ['HelveticaNowDisplay', 'HelveticaNowText'];

    getAssetsToCache() {
        return [
            ...chain(this.data?.sections).map('media').filter('url').value(),
            ...chain(this.data?.sections).map('qr_code.media').filter('url').value(),
        ];
    }

    calculateDuration() {
        if (isNumber(this.lockedSectionIdx)) {
            return calculateSectionsDuration(this.data, this.lockedSectionIdx) || 0;
        }

        return THIRD_SCENE_DELAY_S + THIRD_SCENE_EXIT_DURATION_S + calculateSectionsDuration(this.data) + EXIT_DURATION_S || 0;
    }
}
